// ImageContainer.jsx
import React, { useState, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  IconButton,
  Fade,
} from "@mui/material";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import StarRounded from "@mui/icons-material/StarRounded";
import ArrowRightAltRounded from "@mui/icons-material/ArrowRightAltRounded";
import { 
  ArrowRightRounded, 
  Fullscreen, 
  Download 
} from "@mui/icons-material";
import { motion } from "framer-motion";

const gradientBg =
  "linear-gradient(90deg, rgba(10,25,47,1) 0%, rgba(23,42,69,1) 11%, rgba(23,42,69,1) 45%, rgba(2,12,27,1) 100%)";

/**
 * Props:
 * item: {
 * title: string,
 * subtitle?: string,
 * features?: string[],
 * link: string // image URL (PNG, JPG, etc.)
 * altText: string // Alt text for the image
 * }
 */
const placeholderItem = {
    title: "High-Resolution Asset Preview",
    subtitle: "A stunning render of the latest project design.",
    features: [
      "Optimized for web display",
      "High-contrast color palette",
      "Resolution: 3840 x 2160 (4K)",
      "Layered structure visualization",
    ],
    // Using a placeholder image URL for the asset
    link: "https://placehold.co/1200x675/102547/ffffff?text=Image+Preview", 
    altText: "A conceptual image placeholder showing a futuristic design.",
};

export default function ImageContent({ item = placeholderItem }) {
  const [isLoading, setIsLoading] = useState(true);
  const imageRef = useRef(null);
  
  // State for visual feedback on fullscreen toggle
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!item) return null;

  const handleImageLoaded = () => {
    setIsLoading(false);
  };
  
  const handleFullscreenToggle = () => {
      // Placeholder for actual fullscreen logic (e.g., using Fullscreen API on imageRef)
      setIsFullscreen(!isFullscreen);
      console.log(`Fullscreen toggled: ${!isFullscreen}`);
  };

  const handleDownload = () => {
    if (item.link) {
      const link = document.createElement('a');
      link.href = item.link;
      // Simple filename suggestion based on the title
      const fileExtension = item.link.split('.').pop().split('?')[0]; 
      link.download = `${item.title.replace(/\s/g, '_') || 'image'}.${fileExtension.length > 3 ? 'png' : fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 4, md: 8 },
        mx: "auto",
        maxWidth: 1200,
        fontFamily: 'Inter, sans-serif'
      }}
    >
      {/* Image Container */}
      <Box
        sx={(theme) => ({
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: theme.shadows[6],
          backgroundColor: '#000',
        })}
      >
        {/* Image Display */}
        <Box sx={{ position: 'relative', aspectRatio: '16/9' }}>
          <img
            ref={imageRef}
            src={item.link}
            // Fallback for image loading error
            onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/1200x675/cc0000/ffffff?text=Load+Error"; }} 
            alt={item.altText || item.title || "Showcase Image"}
            onLoad={handleImageLoaded}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
          
          {/* Loading Overlay */}
          <Fade in={isLoading}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                }}
              >
                <Typography variant="h6" color="white" sx={{ p: 2, borderRadius: 1, backgroundColor: 'rgba(255,255,255,0.1)' }}>
                  Loading image...
                </Typography>
              </Box>
            </Fade>

          {/* Image Controls (Floating at the bottom) */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              // Background gradient for control visibility
            }}
          >
          </Box>
        </Box>

        {/* Floating corner badge */}
        <Chip
          icon={<InsightsOutlined />}
          label=""
          color="secondary"
          variant="filled"
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(255,255,255,0.6)",
            backdropFilter: "blur(6px)",
            "& .MuiChip-label": { fontWeight: 700 },
            zIndex: 1,
          }}
        />
      </Box>

      {/* Information panel */}
      <Card
        elevation={4}
        sx={{
          mt: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: (t) => `1px solid ${t.palette.primary.main}`,
        }}
      >
        {/* Gradient headline bar */}
        <Box
          sx={{
            px: 3,
            py: 2,
            background: gradientBg,
            color: "white",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack spacing={0.5}>
              <Typography variant="h5" fontWeight={800} lineHeight={1.2}>
                {item.title}
              </Typography>
              {item.subtitle && (
                <Typography variant="body1" sx={{ opacity: 0.85 }}>
                  {item.subtitle}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                icon={<StarRounded />}
                label="Featured"
                color="warning"
                variant="filled"
                size="small"
                sx={{ fontWeight: 700 }}
              />
              <Chip
                icon={<ArrowRightAltRounded />}
                label="Asset"
                color="secondary"
                variant="outlined"
                size="small"
              />
              <Chip
                icon={<Fullscreen />}
                label={isFullscreen ? "Fullscreen Active" : "View Ready"}
                color="primary"
                variant="outlined"
                size="small"
                onClick={handleFullscreenToggle}
                sx={{ cursor: 'pointer' }}
              />
            </Stack>
          </Stack>
        </Box>

        <CardContent 
          initial={{ opacity: 0}} 
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }} 
          component={motion.div} 
          sx={{ px: { xs: 2, md: 3 }, py: 3 }}
        >
          {/* Feature bullets */}
          <Stack spacing={1.5}>
            <Typography variant="overline" color="text.secondary">
              Key Details
            </Typography>
            <Divider />
            <Stack
              spacing={1}
              sx={{
                "& .feature": {
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontWeight: 600,
                },
              }}
            >
              {(item.features || []).map((text, idx) => (
                <Typography key={idx} className="feature" variant="body1">
                  <ArrowRightRounded
                    fontSize="small"
                    color="secondary"
                    sx={{ mt: "2px" }}
                  />
                  {text}
                </Typography>
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      
     
    </Box>
  );
}